import qs from 'query-string';

import { FilterJoins, FilterOperationsMapForString } from '@constants/filters/filterOperations';

export const redirectToUrl = function (url: string): void {
  if (url.indexOf('http') !== 0) {
    window.open(`https://${url}`, '_blank', 'noopener,noreferrer');
    return;
  }

  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (e) {
    console.error(e);
  }
};

/**
 * Parses `location.search` with the `query-string` library (percent-decoding,
 * repeated keys -> arrays). Keys without a value come back as `''`.
 */
export function parseUrlSearch<T = { [key: string]: string | string[] }>(search: string): T {
  if (!search) {
    return {} as T;
  }

  const parsed = qs.parse(search);
  const result: { [key: string]: string | string[] } = {};

  Object.keys(parsed).forEach((key) => {
    const value = parsed[key];
    result[key] = value === null || value === undefined ? '' : value;
  });

  return result as T;
}

const HAS_SCHEME_OR_CONTROL_CHARS = /^[a-z][a-z0-9+.-]*:|[\r\n\t\0]/i;

/**
 * Returns `raw` only when it is a same-origin, path-only URL (starts with a single
 * `/`, no scheme, no `//host`, no backslash tricks, no control characters);
 * otherwise `fallback`. Use it for every user-controlled `redirect` / `returnTo`
 * parameter before handing it to the router.
 */
export function getSafeInternalPath(raw: unknown, fallback = '/'): string {
  if (typeof raw !== 'string' || raw.length === 0) {
    return fallback;
  }

  let candidate = raw.trim();
  try {
    candidate = decodeURIComponent(candidate);
  } catch (e) {
    return fallback;
  }

  const isPathOnly =
    candidate.startsWith('/') &&
    !candidate.startsWith('//') &&
    !candidate.startsWith('/\\') &&
    !HAS_SCHEME_OR_CONTROL_CHARS.test(candidate);

  return isPathOnly ? raw.trim() : fallback;
}

interface FilterItem {
  type: string;
  value: string;
}

interface ParseQueryFiltersOptions<TFieldMap extends Record<string, string>> {
  queryParamFilters: string | null | undefined;
  filterFieldMap: TFieldMap;
  getOptionsMap: (field: string) => { label: string; value: string | number }[] | undefined;
  currentLength: number;
}

export function parseQueryParamFilters<TFieldMap extends Record<string, any>>({
  queryParamFilters,
  filterFieldMap,
  getOptionsMap,
  currentLength,
}: ParseQueryFiltersOptions<TFieldMap>) {
  if (!queryParamFilters) return [];

  try {
    const parsedFilters: FilterItem[] = JSON.parse(queryParamFilters);

    return parsedFilters
      .map((item) => {
        if (!(item.type in filterFieldMap)) return null;

        const field = filterFieldMap[item.type as keyof TFieldMap];
        const options = getOptionsMap(field);
        const parsedValue = Number.isNaN(parseInt(item.value, 10))
          ? item.value
          : parseInt(item.value, 10);

        const matchedOption = options?.find((opt) => opt.value === parsedValue);

        return {
          order: currentLength,
          field,
          value: options
            ? {
                label: matchedOption?.label ?? String(item.value),
                value: matchedOption?.value ?? parsedValue,
              }
            : parsedValue,
          operation: FilterOperationsMapForString[0].value,
          join: FilterJoins[0].value,
        };
      })
      .filter(Boolean);
  } catch (error) {
    console.error('Error parsing query parameter filters:', error);
    return [];
  }
}

/** `xxx://` at the start: an explicit scheme (case-insensitive). */
const EXPLICIT_SCHEME = /^([a-z][a-z0-9+.-]*):\/\//i;
/** Schemes that take no `//` and must never be read as a host. */
const OPAQUE_SCHEME = /^(mailto|javascript|data|tel|file|sms|blob|about|vbscript):/i;
const WEB_PROTOCOLS = ['http:', 'https:'];

type ParseWebUrlOptions = {
  /** When true, a value without `http://` / `https://` is refused instead of getting `https://`. */
  requireScheme?: boolean;
};

/** A top-level domain: two or more letters (`io`, `com`, `co.uk` ends in `uk`), or an IDN in punycode (`xn--p1ai`). */
const TOP_LEVEL_DOMAIN = /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;

/**
 * A host that names a real website (RES-7348): two or more non-empty labels, not localhost, and a
 * top-level domain of letters (so `a.b`, `x`, `localhost` and IP addresses are refused).
 */
const hasDottedHost = (hostname: string): boolean => {
  const host = hostname.replace(/\.$/, '').toLowerCase();
  if (host === 'localhost' || host.endsWith('.localhost')) {
    return false;
  }
  const labels = host.split('.');
  if (labels.length < 2 || labels.some((label) => label.length === 0)) {
    return false;
  }
  return TOP_LEVEL_DOMAIN.test(labels[labels.length - 1]);
};

/**
 * The one web URL rule of the DFY portals (same rule as the back-end): http or https only
 * (any case), `https://` assumed when no scheme is typed, no `user:password@`, a host with a
 * dot and a real top-level domain (RES-7348: `a.b`, `localhost` and IP addresses are refused).
 * Underscores in host names are accepted. Returns the parsed URL, or null when invalid.
 */
export function parseWebUrl(raw: unknown, opts: ParseWebUrlOptions = {}): URL | null {
  if (typeof raw !== 'string') {
    return null;
  }

  const value = raw.trim();
  if (!value || value.startsWith('/') || OPAQUE_SCHEME.test(value)) {
    return null;
  }

  const scheme = EXPLICIT_SCHEME.exec(value);
  if (scheme && !['http', 'https'].includes(scheme[1].toLowerCase())) {
    return null;
  }
  if (!scheme && opts.requireScheme) {
    return null;
  }

  try {
    const url = new URL(scheme ? value : `https://${value}`);
    if (!WEB_PROTOCOLS.includes(url.protocol)) {
      return null;
    }
    if (url.username || url.password) {
      return null;
    }
    return hasDottedHost(url.hostname) ? url : null;
  } catch {
    return null;
  }
}

export const isWebUrl = (raw: unknown, opts?: ParseWebUrlOptions): boolean =>
  parseWebUrl(raw, opts) !== null;

/**
 * The value to store for a valid web URL: the trimmed input with a lower-case scheme, or with
 * `https://` in front when none was typed. Null when the value is not a valid web URL.
 */
export function normalizeWebUrl(raw: unknown, opts?: ParseWebUrlOptions): string | null {
  if (!isWebUrl(raw, opts)) {
    return null;
  }

  const value = (raw as string).trim();
  const scheme = EXPLICIT_SCHEME.exec(value);
  if (!scheme) {
    return `https://${value}`;
  }
  return `${scheme[1].toLowerCase()}://${value.slice(scheme[0].length)}`;
}
